import { useEffect, useMemo, useRef } from "react"
import type { LoopState, PermanentState } from "../types"
import { locations, routes, getLocationById } from "../game/content"
import { getLocationStatus, type LocationStatus } from "../game/mapEngine"
import { isRouteOpen } from "../game/travelEngine"
import { connectionPath } from "../game/mapConnections"
import { getKnownSchedule, getTravelPaths, type ScheduledEvent } from "../game/scheduleEngine"
import { formatDay } from "../game/calendarEngine"
import { getFactionCrestStyle, getLocationSpriteStyle } from "../game/sprites"
import { getFactionById } from "../game/content"

/** How the war table reads a known-but-not-yet-happened event onto its place. */
type ThreatTone = "reach" | "tight" | "late" | "here"

/**
 * Reduces a scheduled event to the one thing the player is actually asking of
 * the map: can I still get there before it happens, and by how much? This is
 * the same interception arithmetic the Schedule screen spells out in prose —
 * lifted onto the map so the "where" and the "when I can reach it" stop being
 * two screens the player has to hold in their head at once.
 */
function classifyThreat(entry: ScheduledEvent): {
  tone: ThreatTone
  spare: string
  verdict: string
} {
  if (entry.alreadyThere) {
    return { tone: "here", spare: "you are here", verdict: "You are standing where this happens." }
  }
  if (entry.travelDays === null) {
    return {
      tone: "late",
      spare: "no road",
      verdict: "No open road leads here from where you stand.",
    }
  }
  const road = `${entry.travelDays}d of road`
  const spare = entry.daysToSpare ?? 0
  if (spare < 0) {
    const late = Math.abs(spare)
    return {
      tone: "late",
      spare: `${late}d too late`,
      verdict: `${road} — you would arrive ${late} day${late === 1 ? "" : "s"} late.`,
    }
  }
  if (spare === 0) {
    return { tone: "tight", spare: "only just", verdict: `${road} — you would arrive on the day itself.` }
  }
  return {
    tone: "reach",
    spare: `${spare}d to spare`,
    verdict: `${road} — arrive with ${spare} day${spare === 1 ? "" : "s"} to spare.`,
  }
}

const STATUS_CLASS: Record<LocationStatus, string> = {
  unknown: "status-unknown",
  discoveredUnavailable: "status-discovered-unavailable",
  available: "status-available",
  current: "status-current",
  underAttack: "status-under-attack",
  destroyed: "status-destroyed",
  controlledByEnemy: "status-controlled-by-enemy",
  controlledByPlayer: "status-controlled-by-player",
}

const STATUS_SUFFIX: Partial<Record<LocationStatus, string>> = {
  underAttack: "Under Attack",
  destroyed: "Destroyed",
  controlledByEnemy: "Controlled by Enemy",
  controlledByPlayer: "Controlled by Player",
}

/** Every state a marker can be in, so the colours are readable without guessing. */
const LEGEND: Array<{ status: LocationStatus; label: string }> = [
  { status: "current", label: "You are here" },
  { status: "available", label: "One day's road away" },
  { status: "discoveredUnavailable", label: "Known, further off" },
  { status: "underAttack", label: "Under attack" },
  { status: "controlledByEnemy", label: "Held by the enemy" },
  { status: "controlledByPlayer", label: "Held by you" },
  { status: "destroyed", label: "Destroyed" },
]

interface WorldMapScreenProps {
  permanent: PermanentState
  loop: LoopState
  onSelectLocation: (locationId: string) => void
  onAdvanceDay: () => void
  onSimulateDeath: () => void
}

export function WorldMapScreen({
  permanent,
  loop,
  onSelectLocation,
  onAdvanceDay,
  onSimulateDeath,
}: WorldMapScreenProps) {
  // Days are the only currency in this game, so every marker quotes its price
  // in them. Working the distance out by tracing roads across the picture was
  // the arithmetic the map most obviously ought to have been doing itself.
  const travelPaths = useMemo(() => getTravelPaths(permanent, loop), [permanent, loop])

  // Everything the player has foreknowledge of that has not yet happened,
  // keyed to the place it happens so a marker can wear it. A place can host
  // more than one known event; the soonest is the one that decides the plan,
  // so it wins the marker. Past, prevented and completed events are dropped —
  // the map is a planning surface, and only what is still ahead is actionable.
  const threats = useMemo(() => {
    const byLocation = new Map<string, ScheduledEvent>()
    for (const entry of getKnownSchedule(permanent, loop)) {
      if (entry.status !== "upcoming" && entry.status !== "today") continue
      const existing = byLocation.get(entry.event.locationId)
      if (!existing || entry.event.day < existing.event.day) {
        byLocation.set(entry.event.locationId, entry)
      }
    }
    return byLocation
  }, [permanent, loop])

  // The one-line state of the board: how much is coming, and how much of it is
  // still within reach — the aggregate the sidebar's single "next event" line
  // cannot give at a glance.
  const threatCount = threats.size
  const reachableCount = useMemo(() => {
    let reachable = 0
    for (const entry of threats.values()) {
      if (entry.alreadyThere || entry.reachableInTime) reachable += 1
    }
    return reachable
  }, [threats])

  // Places that have surfaced from the fog. When a road unlocks, mapEngine
  // floods discovery to its far end (Phase 7), so a newly-opened route shows up
  // here as a place that just stopped being "unknown". Those markers are inked
  // onto the map with a one-time draw-on rather than simply blinking into
  // existence — progression the player watches accumulate on the page. The
  // first render seeds the set silently, so opening the map never re-inks the
  // whole realm; only genuine new arrivals bloom.
  const shownIds = useMemo(
    () =>
      locations
        .filter((location) => getLocationStatus(location, permanent, loop) !== "unknown")
        .map((location) => location.id),
    [permanent, loop],
  )
  const prevShownRef = useRef<Set<string> | null>(null)
  const newlyShown = useMemo(() => {
    const prev = prevShownRef.current
    const added = new Set<string>()
    if (prev) {
      for (const id of shownIds) if (!prev.has(id)) added.add(id)
    }
    return added
  }, [shownIds])
  useEffect(() => {
    prevShownRef.current = new Set(shownIds)
  }, [shownIds])

  // The roads themselves, drawn under the pins. A route is only inked once both
  // of its ends have surfaced from the fog — otherwise a line would point
  // straight at a place the player has not found yet. Open roads read solid;
  // ones the player knows of but has not yet unlocked read as a faint dashed
  // trail. A road touching where the player stands is lit, so "where can I go
  // from here" is legible at a glance.
  const connections = useMemo(() => {
    const known = new Set(shownIds)
    const out: Array<{ id: string; d: string; open: boolean; active: boolean }> = []
    for (const route of routes) {
      if (!known.has(route.fromLocationId) || !known.has(route.toLocationId)) continue
      const from = getLocationById(route.fromLocationId)
      const to = getLocationById(route.toLocationId)
      if (!from || !to) continue
      out.push({
        id: route.id,
        d: connectionPath(from.position, to.position, route.id),
        open: isRouteOpen(route, permanent),
        active:
          route.fromLocationId === loop.currentLocationId ||
          route.toLocationId === loop.currentLocationId,
      })
    }
    return out
  }, [shownIds, permanent, loop.currentLocationId])

  return (
    <div className="screen world-map-screen">
      <h1>World Map</h1>

      {/* The war table's ledger line: what the player knows is coming, and how
          much of it they could still ride out to meet. Only shown once there is
          any foreknowledge to plan around. */}
      {threatCount > 0 && (
        <p className="map-threat-summary">
          <span className="map-threat-summary-count">{threatCount}</span> known event
          {threatCount === 1 ? "" : "s"} still ahead —{" "}
          {reachableCount === 0 ? (
            <span className="intercept-bad">none you could still reach in time.</span>
          ) : reachableCount === threatCount ? (
            <span className="intercept-good">
              all still within reach from where you stand.
            </span>
          ) : (
            <>
              <span className="intercept-good">{reachableCount}</span> still within reach
              from where you stand.
            </>
          )}
        </p>
      )}

      <div className="map-container">
        <img src="/img/game_map.png" alt="World map" className="map-image" />
        {/* Roads under the pins. The viewBox is a flat 0–100 grid stretched to
            the image, matching the percentage coordinates the markers use, so a
            road's ends land exactly on their two pins. Strokes are kept a
            constant screen width (non-scaling-stroke) despite that stretch. */}
        <svg
          className="map-connections"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {connections.map((connection) => (
            <g
              key={connection.id}
              className={[
                "map-connection",
                connection.open ? "is-open" : "is-locked",
                connection.active ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <path
                className="map-connection-casing"
                d={connection.d}
                vectorEffect="non-scaling-stroke"
              />
              <path
                className="map-connection-line"
                d={connection.d}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ))}
        </svg>
        {locations.map((location) => {
          const status = getLocationStatus(location, permanent, loop)
          const suffix = STATUS_SUFFIX[status]
          const showLabel = status !== "unknown"
          const path = travelPaths.get(location.id)
          const distance =
            status === "current" || status === "unknown" || !path
              ? null
              : `${path.days} day${path.days === 1 ? "" : "s"}`
          // A place the player has found shows a small portrait on hover; a
          // place still in the fog reveals nothing.
          const spriteStyle = showLabel ? getLocationSpriteStyle(location.id) : null
          // Who holds this place, flown as its heraldic crest once found: the
          // owning realm's banner, so allegiance reads off the map at a glance
          // rather than only from the marker's colour. The status dot still
          // carries the player's relationship to it (reachable, under attack,
          // held); the crest names the power whose ground it is.
          const owner = showLabel && location.factionId ? getFactionById(location.factionId) : null
          const ownerCrest = owner ? getFactionCrestStyle(owner.id) : null
          // A known event still to come, read onto its place: the day it lands
          // and whether the player could still ride out and meet it.
          const threat = threats.get(location.id)
          const threatInfo = threat ? classifyThreat(threat) : null
          return (
            <button
              key={location.id}
              type="button"
              className={[
                "map-marker",
                STATUS_CLASS[status],
                newlyShown.has(location.id) ? "map-marker-inking" : "",
                threat ? "map-marker-has-threat" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                left: `${location.position.x}%`,
                top: `${location.position.y}%`,
              }}
              title={
                status === "unknown"
                  ? "Somewhere you have not found yet"
                  : [
                      location.name,
                      distance && `${distance} away`,
                      suffix,
                      threat &&
                        threatInfo &&
                        `${formatDay(threat.event.day)}: ${threatInfo.verdict}`,
                    ]
                      .filter(Boolean)
                      .join(" — ")
              }
              onClick={() => onSelectLocation(location.id)}
            >
              <span className="map-marker-pin">
                <span className="map-marker-dot" />
                {threat && threatInfo && (
                  <span
                    className={`map-marker-threat threat-${threatInfo.tone}`}
                    aria-hidden="true"
                  >
                    <span className="map-marker-threat-day">{formatDay(threat.event.day)}</span>
                    <span className="map-marker-threat-spare">
                      {threat.daysUntil > 0
                        ? `in ${threat.daysUntil}d · ${threatInfo.spare}`
                        : `today · ${threatInfo.spare}`}
                    </span>
                  </span>
                )}
              </span>
              {spriteStyle && (
                <span className="map-marker-tooltip" aria-hidden="true">
                  <span className="map-marker-tooltip-image" style={spriteStyle} />
                  <span className="map-marker-tooltip-name">{location.name}</span>
                  {owner && (
                    <span className="map-marker-tooltip-owner">
                      {ownerCrest && (
                        <span
                          className="map-marker-tooltip-owner-crest"
                          style={ownerCrest}
                          aria-hidden="true"
                        />
                      )}
                      Held by {owner.name}
                    </span>
                  )}
                  {threat && threatInfo && (
                    <span className={`map-marker-tooltip-threat threat-${threatInfo.tone}`}>
                      {formatDay(threat.event.day)} — {threatInfo.verdict}
                    </span>
                  )}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <ul className="map-legend">
        {LEGEND.map(({ status, label }) => (
          <li key={status}>
            <span className={["map-legend-dot", STATUS_CLASS[status]].join(" ")} />
            {label}
          </li>
        ))}
      </ul>

      <div className="world-map-actions">
        <button type="button" onClick={onAdvanceDay}>
          Advance One Day
        </button>
        {/* Ending the loop is a development affordance, not a player action.
            It used to sit one misclick away from "Advance One Day", where the
            two most consequential buttons on the screen were adjacent and
            visually alike. Kept for testing, but only in dev builds. */}
        {import.meta.env.DEV && (
          <button
            type="button"
            className="danger debug-action"
            onClick={onSimulateDeath}
          >
            Simulate Death (debug)
          </button>
        )}
      </div>
    </div>
  )
}
